import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../../services/api';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Switch,
    TextField,
    Typography,
    useTheme,
    FormControlLabel
} from '@mui/material';

import {
    IconApps,
    IconSearch,
    IconPlus,
    IconEdit,
    IconTrash,
    IconDeviceFloppy,
    IconX,
    IconWorld,
    IconKey,
    IconBrandGoogle,
    IconWebhook,
    IconChevronRight,
    IconRocket,
    IconArrowLeft
} from '@tabler/icons';

import { motion } from 'framer-motion';
import { useHistory, useParams } from 'react-router-dom';

// import axios from 'axios';

const MotionCard = motion(Card);

// const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// const api = axios.create({
//     baseURL: API_URL
// });

// api.interceptors.request.use((config) => {
//     const token = localStorage.getItem('access_token');

//     if (token) {
//         config.headers.Authorization = `Bearer ${token}`;
//     }

//     return config;
// });

const APP_TYPES = [
    {
        value: 'crm',
        label: 'CRM'
    },
    {
        value: 'chatbot',
        label: 'Chatbot'
    },
    {
        value: 'automation',
        label: 'Automação'
    },
    {
        value: 'api',
        label: 'API'
    }
];

const SystemApplications = () => {
    const theme = useTheme();

    const [apps, setApps] = useState([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');

    const history = useHistory();

    const [status, setStatus] = useState({
        type: '',
        message: ''
    });

    const [openModal, setOpenModal] = useState(false);

    const [editingApp, setEditingApp] = useState(null);

    const [form, setForm] = useState({
        name: '',
        slug: '',
        domain: '',
        app_type: 'crm',
        description: '',
        active: true,
        webhook_url: ''
    });

    useEffect(() => {
        loadApps();
    }, []);

    const loadApps = async () => {
        setLoading(true);

        try {
            const response = await api.get('v1/apps/');

            setApps(response.data?.apps || []);
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: 'Erro ao carregar aplicativos.'
            });
        } finally {
            setLoading(false);
        }
    };

    const filteredApps = useMemo(() => {
        if (!search.trim()) {
            return apps;
        }

        const term = search.toLowerCase();

        return apps.filter(
            (app) =>
                app.name?.toLowerCase().includes(term) || app.slug?.toLowerCase().includes(term) || app.domain?.toLowerCase().includes(term)
        );
    }, [search, apps]);

    const handleOpenCreate = () => {
        setEditingApp(null);

        setForm({
            name: '',
            slug: '',
            domain: '',
            app_type: 'crm',
            description: '',
            active: true,
            webhook_url: ''
        });

        setOpenModal(true);
    };

    const handleOpenEdit = (app) => {
        setEditingApp(app);

        setForm({
            name: app.name || '',
            slug: app.slug || '',
            domain: app.domain || '',
            app_type: app.app_type || 'crm',
            description: app.description || '',
            active: app.active ?? true,
            webhook_url: app.webhook_url || ''
        });

        setOpenModal(true);
    };

    const handleSave = async () => {
        try {
            if (editingApp) {
                await api.patch(`v1/apps/${editingApp.id}/`, form);

                setStatus({
                    type: 'success',
                    message: 'Aplicativo atualizado com sucesso.'
                });
            } else {
                await api.post('v1/apps/', form);

                setStatus({
                    type: 'success',
                    message: 'Aplicativo criado com sucesso.'
                });
            }

            setOpenModal(false);

            loadApps();
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: 'Erro ao salvar aplicativo.'
            });
        }
    };

    const handleDelete = async (appId) => {
        const confirmDelete = window.confirm('Deseja realmente excluir este aplicativo?');

        if (!confirmDelete) {
            return;
        }

        try {
            await api.delete(`v1/apps/${appId}/`);

            setApps((prev) => prev.filter((a) => a.id !== appId));

            setStatus({
                type: 'success',
                message: 'Aplicativo excluído com sucesso.'
            });
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: 'Erro ao excluir aplicativo.'
            });
        }
    };

    return (
        <Box>
            {/* HERO */}

            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none'
                }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '28px',
                    p: {
                        xs: 3,
                        md: 5
                    },
                    mb: 4,
                    mt: 2,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#1e293b 100%)'
                            : 'linear-gradient(135deg,#eef2ff 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.08)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 340,
                        height: 340,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(59,130,246,0.12))',
                        top: -120,
                        right: -100,
                        filter: 'blur(40px)'
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Chip
                        icon={<IconApps size={14} />}
                        label="Aplicativos"
                        sx={{
                            mb: 2,
                            fontWeight: 700
                        }}
                    />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: '2rem',
                                md: '3rem'
                            },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#4f46e5,#2563eb)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Aplicativos do Sistema
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: 700,
                            lineHeight: 1.8,
                            mb: 4
                        }}
                    >
                        Crie aplicativos para ambientes de testes ou ambientes de produção.
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap'
                        }}
                    >
                        <OutlinedInput
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Buscar aplicativo..."
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconSearch size={18} />
                                </InputAdornment>
                            }
                            sx={{
                                flex: 1,
                                minWidth: 280,
                                height: 56,
                                borderRadius: '18px',
                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={handleOpenCreate}
                            sx={{
                                height: 56,
                                px: 3,
                                borderRadius: '18px',
                                textTransform: 'none',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg,#4f46e5,#2563eb)',
                                boxShadow: '0 10px 25px rgba(79,70,229,0.25)'
                            }}
                        >
                            Novo Aplicativo
                        </Button>
                    </Box>
                </Box>
            </Box>

            {status.message && (
                <Alert
                    severity={status.type}
                    sx={{
                        mb: 3,
                        borderRadius: '16px'
                    }}
                >
                    {status.message}
                </Alert>
            )}

            {/* GRID */}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 10
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredApps.map((app, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={app.id}>
                            <MotionCard
                                initial={{
                                    opacity: 0,
                                    y: 20
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0
                                }}
                                transition={{
                                    delay: index * 0.03
                                }}
                                whileHover={{
                                    y: -5
                                }}
                                sx={{
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    height: '100%',
                                    border:
                                        theme.palette.mode === 'dark'
                                            ? '1px solid rgba(255,255,255,0.06)'
                                            : '1px solid rgba(15,23,42,0.06)',
                                    background: theme.palette.mode === 'dark' ? 'linear-gradient(180deg,#111827 0%,#0f172a 100%)' : '#fff',
                                    boxShadow:
                                        theme.palette.mode === 'dark' ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(15,23,42,0.06)'
                                }}
                            >
                                <CardActionArea
                                    onClick={() => handleOpenEdit(app)}
                                    sx={{
                                        height: '100%'
                                    }}
                                >
                                    <CardContent
                                        sx={{
                                            p: 3
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                mb: 2
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 62,
                                                    height: 62,
                                                    background: 'linear-gradient(135deg,#4f46e5,#2563eb)',
                                                    boxShadow: '0 10px 25px rgba(79,70,229,0.25)'
                                                }}
                                            >
                                                <IconApps size={28} />
                                            </Avatar>

                                            <Chip
                                                label={app.is_default === true ? 'online' : 'offline'}
                                                size="small"
                                                color={app.is_default === true ? 'success' : 'default'}
                                                sx={{
                                                    fontWeight: 700
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 1
                                            }}
                                        >
                                            {app.displayName}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 2,
                                                minHeight: 44
                                            }}
                                        >
                                            {app.subdomain || 'Sem subdomínio configurado.'}
                                        </Typography>

                                        <Divider
                                            sx={{
                                                my: 2
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1.2
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <IconWorld size={16} />

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {app.domain}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <IconKey size={16} />

                                                <Typography variant="caption">{app.id}</Typography>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                mt: 3,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                Abrir aplicativo
                                            </Typography>

                                            <IconChevronRight size={18} />
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            </MotionCard>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* MODAL */}

            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: '24px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontWeight: 800
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <IconRocket size={22} />

                        {editingApp ? 'Editar Aplicativo' : 'Novo Aplicativo'}
                    </Box>

                    <IconButton onClick={() => setOpenModal(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nome do aplicativo"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        name: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Slug"
                                value={form.slug}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        slug: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Domínio"
                                value={form.domain}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        domain: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Tipo"
                                value={form.app_type}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        app_type: e.target.value
                                    })
                                }
                            >
                                {APP_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Descrição"
                                value={form.description}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        description: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Webhook URL"
                                value={form.webhook_url}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        webhook_url: e.target.value
                                    })
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconWebhook size={18} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: '16px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e2e8f0'
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.active}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    active: e.target.checked
                                                })
                                            }
                                        />
                                    }
                                    label="Aplicativo ativo"
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        justifyContent: 'space-between'
                    }}
                >
                    {editingApp ? (
                        <Button
                            color="error"
                            startIcon={<IconTrash size={18} />}
                            onClick={() => {
                                setOpenModal(false);

                                handleDelete(editingApp.id);
                            }}
                            sx={{
                                borderRadius: '12px',
                                textTransform: 'none'
                            }}
                        >
                            Excluir
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Button
                        variant="contained"
                        startIcon={<IconDeviceFloppy size={18} />}
                        onClick={handleSave}
                        sx={{
                            borderRadius: '14px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#4f46e5,#2563eb)',
                            boxShadow: '0 10px 25px rgba(79,70,229,0.25)'
                        }}
                    >
                        Salvar Aplicativo
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SystemApplications;
